import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import {
  SETTING_API_01,
  SETTING_API_02,
  SETTING_API_05,
  SETTING_API_06,
  SETTING_API_07,
  SETTING_API_08,
} from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import { 
  CON_STATUS_ACTIVE, 
  CON_VALUE_ZERO,
  CON_MENUICON_WIDTH,
  CON_MENUICON_HEIGHT,
  CON_MAX_FILESIZE_ITEMIMG 
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import ImageCropper from "../../components/common/ImageCropper.tsx";

// Types and Interfaces
interface CategoryItem {
  catcode?: string | number;
  catname?: string;
}

interface SubCategoryItem {
  subcatcode?: string | number;
  subcatname?: string;
}

interface BrandItem {
  brandcode?: string | number;
  brandname?: string;
}

interface PackageItem {
  packagecode?: string | number;
  packagename?: string;
}

interface MeasuringUnitItem {
  immeasuringunitcode?: string | number;
  immeasuringunit?: string;
}

interface ProductItem {
  productcode?: string | number;
  productname?: string;
  catcode?: string | number;
  subcatcode?: string | number;
  brandcode?: string | number;
  packagecode?: string | number;
  immeasuringunitcode?: string | number;
  minorderlevel?: number | string;
  productsize?: number | string;
  packageunits?: number | string;
  imagepath?: string;
  descrip?: string;
  catname?: string;
  subcatname?: string;
  brandname?: string;
  packagename?: string;
  immeasuringunit?: string;
}

interface ProductDetails {
  productname: string;
  catcode: string | number;
  subcatcode: string | number;
  brandcode: string | number;
  packagecode: string | number;
  productcode: string | number;
  minorderlevel: number | string;
  productsize: number | string;
  packageunits: number | string;
  immeasuringunitcode: string | number;
  imagepath: string;
  catname: string;
  subcatname: string;
  brandname: string;
  packagename: string;
  immeasuringunit: string;
  descrip: string;
}

interface ErrorState {
  productname?: string;
  catname?: string;
  subcatname?: string;
  brandname?: string;
  packagename?: string;
  immeasuringunit?: string;
  minorderlevel?: string;
  productsize?: string;
  packageunits?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  RESTCODE?: string;
  USERNAME?: string;
  COMPCODE?: string;
  STAFFMAPLOCODE?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: ProductItem;
  actionText?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function CreateProducts(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const [data, setData] = useState<ProductItem[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const location = useLocation();
  const { selectedItem } = (location.state as LocationState) || {};
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, RESTCODE, USERNAME, COMPCODE, STAFFMAPLOCODE } =
    SYS_USER.USER_PARAM || {};
  const isEdit = (location.state as LocationState)?.actionText === "Edit";

  const [datacat, setDatacat] = useState<CategoryItem[] | null>(null);
  const [datasubcat, setDatasubcat] = useState<SubCategoryItem[] | null>(null);
  const [databrand, setDatabrand] = useState<BrandItem[] | null>(null);
  const [datapack, setDatapack] = useState<PackageItem[] | null>(null);
  const [datamou, setDatamou] = useState<MeasuringUnitItem[] | null>(null);

  const [filePath, setFilePath] = useState<string>("");
  const [croppedImg, setCroppedImg] = useState<string>("");
  const [error, setError] = useState<ErrorState>({});

  const [selectedItemDetails, setSelectedItemDetails] = useState<ProductDetails>({
    productname: "",
    catcode: "",
    subcatcode: "",
    brandcode: "",
    packagecode: "",
    productcode: "",
    minorderlevel: CON_VALUE_ZERO,
    productsize: CON_VALUE_ZERO,
    packageunits: CON_VALUE_ZERO,
    immeasuringunitcode: "",
    imagepath: "",
    catname: "",
    subcatname: "",
    brandname: "",
    packagename: "",
    immeasuringunit: "",
    descrip: "",
  });

  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        catcode: selectedItem.catcode || "",
        subcatcode: selectedItem.subcatcode || "",
        catname: selectedItem.catname || "",
        subcatname: selectedItem.subcatname || "",
        brandname: selectedItem.brandname || "",
        brandcode: selectedItem.brandcode || "",
        packagename: selectedItem.packagename || "",
        packagecode: selectedItem.packagecode || "",
        productname: selectedItem.productname || "",
        descrip: selectedItem.descrip || "",
        productcode: selectedItem.productcode || "",
        minorderlevel: selectedItem.minorderlevel || CON_VALUE_ZERO,
        productsize: selectedItem.productsize || CON_VALUE_ZERO,
        immeasuringunit: selectedItem.immeasuringunit || "",
        immeasuringunitcode: selectedItem.immeasuringunitcode || "",
        packageunits: selectedItem.packageunits || CON_VALUE_ZERO,
        imagepath: selectedItem.imagepath || "",
      });
      console.log("Selected Item Details:", selectedItem.imagepath);
    }
  }, [selectedItem]);

  console.log("Selected Item Details:", selectedItemDetails);

  useEffect(() => {
    setPageData({
      title: "Product",
    } as PageData);
  }, [setPageData]);

  const goBackToView = (): void => {
    navigate("/vCreateProducts");
  };

  const handleCroppedImage = (img: string): void => {
    setCroppedImg(img);
    console.log("Received cropped image from child:", img);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Prevent initial space only
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate max length
    if (typeof value === "string" && value.length > 30) {
      setError((prevError) => ({
        ...prevError,
        [name]: "Input cannot exceed 30 characters.",
      }));
      return;
    }

    // Validate empty string
    if (typeof value === "string" && trimmedValue === "") {
      setError((prevError) => ({
        ...prevError,
        [name]: "Input cannot be empty or just spaces.",
      }));
    } else {
      setError((prevError) => ({
        ...prevError,
        [name]: "",
      }));
    }

    // Always update the value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handlenumInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Prevent initial space only
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate numeric input (allow integers and floats)
    const isNumeric = /^[0-9]*\.?[0-9]*$/.test(value);
    if (!isNumeric) {
      setError((prevError) => ({
        ...prevError,
        [name]: "Input must contain only numbers or decimals.",
      }));
      return;
    }

    // Clear the value if it is "0" when focused
    if (value === "0") {
      setSelectedItemDetails((prevDetails) => ({
        ...prevDetails,
        [name]: "",
      }));
      return;
    }

    // Clear error if input is valid
    setError((prevError) => ({
      ...prevError,
      [name]: "",
    }));

    // Always update the value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleInputChangedes = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Prevent initial space only
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate empty string
    if (typeof value === "string" && trimmedValue === "") {
      setError((prevError) => ({
        ...prevError,
        [name]: "Input cannot be empty or just spaces.",
      }));
    } else {
      setError((prevError) => ({
        ...prevError,
        [name]: "",
      }));
    }

    // Always update the value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_07, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setData(result as ProductItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);
    const {
      productname,
      catname,
      subcatname,
      brandname,
      packagename,
      immeasuringunit,
    } = selectedItemDetails;

    const requiredFields = [
      { name: "productname", value: productname, label: "Product Name" },
      { name: "catname", value: catname, label: "Category Name" },
      { name: "subcatname", value: subcatname, label: "Subcategory Name" },
      { name: "brandname", value: brandname, label: "Brand Name" },
      { name: "packagename", value: packagename, label: "Package Name" },
      {
        name: "immeasuringunit",
        value: immeasuringunit,
        label: "Measuring Unit",
      },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) => !field.value
    );
    
    if (missingRequiredFields.length > 0) {
      const newError: ErrorState = {};
      missingRequiredFields.forEach((field) => {
        newError[field.name] = `${field.label} is required.`;
      });
      setError(newError);

      setAlertType("error");
      setAlertMessage("Please Fill All the Required Fields.");
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    if (data) {
      const isDuplicate = data.some((item) => {
        if (
          item.productcode !== selectedItemDetails.productcode &&
          item.productname?.toLowerCase() === productname.toLowerCase()
        ) {
          return true;
        } else {
          return false;
        }
      });

      if (isDuplicate) {
        setAlertMessage("Product Already Exists.");
        setAlertType("warning");
        setAlertOpen(true);
        setIsButtonDisabled(false);
        return;
      }
    }

    const BRAND_CODE = selectedItem ? selectedItem.productcode : CON_VALUE_ZERO;
    console.log("Brand Code:", BRAND_CODE);

    const staticValues = {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      BYTEARRAY: croppedImg.length > 0 ? String(croppedImg.split(",")[1]) : "",
      businesstypecode: "O",
      imagepath: filePath,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
    };

    const payload = {
      ...selectedItemDetails,
      ...staticValues,
      productcode: BRAND_CODE,
    };

    try {
      console.log("Payload for API call:", payload);
      const responseData = await externalpostApi(SETTING_API_07, payload);
      console.log("Success:", responseData);
      setAlertMessage(
        `${isEdit ? "Updated Successfully!" : "Created Successfully!"}`
      );
      setAlertType("success");
      setAlertOpen(true);

      setSelectedItemDetails({
        productname: "",
        catcode: "",
        subcatcode: "",
        brandcode: "",
        packagecode: "",
        productcode: "",
        minorderlevel: "",
        productsize: "",
        packageunits: "",
        immeasuringunitcode: "",
        imagepath: "",
        catname: "",
        subcatname: "",
        brandname: "",
        packagename: "",
        immeasuringunit: "",
        descrip: "",
      });
      
      setTimeout(() => {
        navigate("/vCreateProducts");
      }, 2000);
    } catch (error) {
      const err = error as ApiError;
      console.error("Error:", err);
      setAlertType("error");
      setAlertMessage(err.response?.data?.message || "An unexpected error occurred.");
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const handleCancel = (): void => {
    setSelectedItemDetails({
      productname: "",
      catcode: "",
      subcatcode: "",
      brandcode: "",
      packagecode: "",
      productcode: "",
      minorderlevel: "",
      productsize: "",
      packageunits: "",
      immeasuringunitcode: "",
      imagepath: "",
      catname: "",
      subcatname: "",
      brandname: "",
      packagename: "",
      immeasuringunit: "",
      descrip: "",
    });

    setCroppedImg("");
    setFilePath("");
    setAlertOpen(false);
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_02, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setDatacat(result as CategoryItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_08, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setDatasubcat(result as SubCategoryItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_01, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setDatabrand(result as BrandItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_06, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setDatapack(result as PackageItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_05, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setDatamou(result as MeasuringUnitItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Helper function to find the selected value or return undefined
  const findSelectedCategory = (): CategoryItem | undefined => {
    if (!Array.isArray(datacat) || !selectedItemDetails.catname) return undefined;
    return datacat.find(
      (item) => item.catname === selectedItemDetails.catname
    );
  };

  const findSelectedSubCategory = (): SubCategoryItem | undefined => {
    if (!Array.isArray(datasubcat) || !selectedItemDetails.subcatcode) return undefined;
    return datasubcat.find(
      (item) => item.subcatcode === selectedItemDetails.subcatcode
    );
  };

  const findSelectedBrand = (): BrandItem | undefined => {
    if (!Array.isArray(databrand) || !selectedItemDetails.brandcode) return undefined;
    return databrand.find(
      (item) => item.brandcode === selectedItemDetails.brandcode
    );
  };

  const findSelectedPackage = (): PackageItem | undefined => {
    if (!Array.isArray(datapack) || !selectedItemDetails.packagecode) return undefined;
    return datapack.find(
      (item) => item.packagecode === selectedItemDetails.packagecode
    );
  };

  const findSelectedMeasuringUnit = (): MeasuringUnitItem | undefined => {
    if (!Array.isArray(datamou) || !selectedItemDetails.immeasuringunitcode) return undefined;
    return datamou.find(
      (item) => item.immeasuringunitcode === selectedItemDetails.immeasuringunitcode
    );
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to All Items" arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            Home
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Product
          </Link>
        </Breadcrumbs>
      </Box>
      
      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 8, sm: 10, md: 10, lg: 10, xl: 10 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {isEdit ? "Edit Product" : "Create Product"}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>
        
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={styles.add_form_page_container}
        >
          <Grid container spacing={2} sx={styles.add_form_page_grid_container}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_productname"
                label="Product Name"
                variant="outlined"
                fullWidth
                size="small"
                required
                name="productname"
                value={selectedItemDetails.productname}
                onChange={handleInputChange}
                error={!!error.productname}
                helperText={error.productname}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Autocomplete
                id="act_catname"
                options={Array.isArray(datacat) ? datacat : []}
                getOptionLabel={(option: CategoryItem) => option.catname || ""}
                disableClearable
                value={findSelectedCategory()}
                onChange={(event: React.SyntheticEvent, newValue: CategoryItem | null) => {
                  handleInputChange({
                    target: {
                      name: "catcode",
                      value: newValue ? newValue.catcode || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  
                  handleInputChange({
                    target: {
                      name: "catname",
                      value: newValue ? newValue.catname || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                renderInput={(params) => {
                  // Remove the id from params to avoid duplication
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_catname"
                      {...restParams}
                      label={
                        <span>
                          Category Name<span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.catname}
                      helperText={error?.catname}
                      size="small"
                      name="catcode"
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "red" },
                      }}
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Autocomplete
                id="act_subcatname"
                options={Array.isArray(datasubcat) ? datasubcat : []}
                getOptionLabel={(option: SubCategoryItem) => option.subcatname || ""}
                disableClearable
                value={findSelectedSubCategory()}
                onChange={(event: React.SyntheticEvent, newValue: SubCategoryItem | null) => {
                  handleInputChange({
                    target: {
                      name: "subcatcode",
                      value: newValue ? newValue.subcatcode || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  
                  handleInputChange({
                    target: {
                      name: "subcatname",
                      value: newValue ? newValue.subcatname || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_subcatname"
                      {...restParams}
                      label={
                        <span>
                          Subcategory Name<span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.subcatname}
                      helperText={error?.subcatname}
                      size="small"
                      name="subcatcode"
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "red" },
                      }}
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Autocomplete
                id="act_brandname"
                options={Array.isArray(databrand) ? databrand : []}
                getOptionLabel={(option: BrandItem) => option.brandname || ""}
                disableClearable
                value={findSelectedBrand()}
                onChange={(event: React.SyntheticEvent, newValue: BrandItem | null) => {
                  handleInputChange({
                    target: {
                      name: "brandcode",
                      value: newValue ? newValue.brandcode || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  
                  handleInputChange({
                    target: {
                      name: "brandname",
                      value: newValue ? newValue.brandname || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_brandname"
                      {...restParams}
                      label={
                        <span>
                          Brand Name<span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.brandname}
                      helperText={error?.brandname}
                      size="small"
                      name="brandcode"
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "red" },
                      }}
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Autocomplete
                id="act_packagename"
                options={Array.isArray(datapack) ? datapack : []}
                getOptionLabel={(option: PackageItem) => option.packagename || ""}
                disableClearable
                value={findSelectedPackage()}
                onChange={(event: React.SyntheticEvent, newValue: PackageItem | null) => {
                  handleInputChange({
                    target: {
                      name: "packagecode",
                      value: newValue ? newValue.packagecode || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  
                  handleInputChange({
                    target: {
                      name: "packagename",
                      value: newValue ? newValue.packagename || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_packagename"
                      {...restParams}
                      label={
                        <span>
                          Package Name<span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.packagename}
                      helperText={error?.packagename}
                      size="small"
                      name="packagecode"
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "red" },
                      }}
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Autocomplete
                id="act_immeasuringunit"
                options={Array.isArray(datamou) ? datamou : []}
                getOptionLabel={(option: MeasuringUnitItem) => option.immeasuringunit || ""}
                disableClearable
                value={findSelectedMeasuringUnit()}
                onChange={(event: React.SyntheticEvent, newValue: MeasuringUnitItem | null) => {
                  handleInputChange({
                    target: {
                      name: "immeasuringunitcode",
                      value: newValue ? newValue.immeasuringunitcode || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                  
                  handleInputChange({
                    target: {
                      name: "immeasuringunit",
                      value: newValue ? newValue.immeasuringunit || "" : "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_measuringunit"
                      {...restParams}
                      label={
                        <span>
                          Measuring unit<span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.immeasuringunit}
                      helperText={error?.immeasuringunit}
                      size="small"
                      name="immeasuringunitcode"
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "red" },
                      }}
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_minorderlevel"
                label="Minimum Order Level"
                variant="outlined"
                fullWidth
                size="small"
                name="minorderlevel"
                value={selectedItemDetails.minorderlevel}
                onChange={handlenumInputChange}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                  if (e.target.value === "0") {
                    handlenumInputChange({
                      target: {
                        name: "minorderlevel",
                        value: "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                error={!!error.minorderlevel}
                helperText={error.minorderlevel}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_productsize"
                label="Product Size"
                variant="outlined"
                fullWidth
                size="small"
                name="productsize"
                value={selectedItemDetails.productsize}
                onChange={handlenumInputChange}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                  if (e.target.value === "0") {
                    handlenumInputChange({
                      target: {
                        name: "productsize",
                        value: "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                error={!!error.productsize}
                helperText={error.productsize}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_packageunits"
                label="Package Units"
                variant="outlined"
                fullWidth
                size="small"
                name="packageunits"
                value={selectedItemDetails.packageunits}
                onChange={handlenumInputChange}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                  if (e.target.value === "0") {
                    handlenumInputChange({
                      target: {
                        name: "packageunits",
                        value: "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                error={!!error.packageunits}
                helperText={error.packageunits}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_descrip"
                label="Description"
                variant="outlined"
                fullWidth
                size="small"
                name="descrip"
                value={selectedItemDetails.descrip}
                onChange={handleInputChangedes}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    ml: -52,
                    display: "flex",
                  }}
                >
                  <ImageCropper
                    onCropComplete={handleCroppedImage}
                    FIXED_WIDTH={CON_MENUICON_WIDTH}
                    FIXED_HEIGHT={CON_MENUICON_HEIGHT}
                    MAX_FILESIZE={CON_MAX_FILESIZE_ITEMIMG}
                    setSnackbarMessage={setAlertMessage}
                    setOpenErrorSnackbar={setAlertOpen}
                    setFilePath={setFilePath}
                  />
                </Box>
                <Box
                  sx={{
                    width: 210,
                    height: 210,
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f9f9f9",
                    flexShrink: 0,
                  }}
                >
                  {croppedImg ? (
                    <img
                      src={croppedImg}
                      alt="Cropped Result"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : selectedItemDetails.imagepath ? (
                    <img
                      src={selectedItemDetails.imagepath}
                      alt="Selected Item"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        fontSize: 14,
                        color: "#888",
                        textAlign: "center",
                      }}
                    >
                      No Image Available
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_clear"
            variant="outlined"
            size="medium"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            Clear
          </Button>
          <Button
            id="btn_confirm"
            variant="contained"
            size="medium"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            onClick={handleConfirmClick}
            disabled={isButtonDisabled}
          >
            {isEdit ? "Modify" : "Confirm"}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateProducts;